import CaptionPlayer from './CaptionPlayer.js';
import ObjectiveList from '../ui/ObjectiveList.js';
import { toRunway } from './runwayFrame.js';
import { TRAINING } from '../core/SimConfig.js';
import { trainingVoiceUrl } from '../audio/soundManifest.js';

const NM_PER_METER = 1 / 1852;

/**
 * Runtime for a guided flight-school mission.
 *
 * A mission is a flat, ordered list of objectives (see js/training/missions/).
 * Exactly one is active at a time; when it's satisfied it ticks green, slides
 * off the objective board, and the next one arrives a beat later. Objectives
 * come in four flavours, which compose rather than exclude each other:
 *
 *   info      — instructor patter with nothing to do; clears once the lines
 *               it queued have finished playing.
 *   task      — an action to perform; clears on its `complete(ctx)` predicate.
 *   waypoint  — a place to be; clears on entering `waypoint`'s capture volume,
 *               AND'd with `complete(ctx)` when both are given.
 *   destroy   — training targets to kill; clears when they're all down.
 *
 * Any objective may also carry a `waypoint` marked `guide: true`, which draws
 * the spatial marker without making it a condition — that's how the downwind
 * "gear and flaps" objective still shows the player where the abeam point is.
 * It may also carry `spawn`: a list of scenario-shaped spawn entries placed as
 * the objective becomes active, so a mission's targets appear when the script
 * reaches them instead of all at launch (see _enter for the ordering rule).
 * A waypoint carrying `track` follows a live enemy rather than a fixed point
 * (see _syncTrackedWaypoint), which is what makes an airborne target a usable
 * objective at all.
 *
 * Instructor voice-over is addressed by an objective's `audio` key rather than
 * its `id`, so the recordings can carry the script's ordering in their names
 * (`01_intro`, `08a_climbOut`) independently of the ids the predicates use. Each
 * key expands to up to three files — the briefing, the completion credit, and
 * one per hint (see {@link _voice}) — which CaptionPlayer plays across the whole
 * group of caption lines they cover. A hint shared between objectives (the
 * altitude/steer nudges the circuit legs reuse) names its own file instead.
 *
 * Objectives can also carry `hints`: assistive/corrective lines that fire while
 * the objective is in progress and their condition has held for a while ("you're
 * low, increase throttle"). Hints only speak while the instructor is otherwise
 * quiet, so they can never stack on top of a briefing.
 *
 * Everything here runs off the sim clock, so the whole mission — captions,
 * hint timers, hold timers — freezes cleanly with the game when paused.
 */
export default class TrainingMission {
  constructor({ definition, app, aircraft, controller, terrainCollider = null, enemyManager = null }) {
    this.definition = definition;
    this.app = app;
    this.aircraft = aircraft;
    this.controller = controller;
    this.terrainCollider = terrainCollider;
    this.enemyManager = enemyManager;

    /** Fired once the last objective clears. App reacts with the completion overlay. */
    this.onComplete = null;

    this.captions = new CaptionPlayer({
      masterVolume: app?.settings?.masterVolume ?? 1,
      voiceVolume: app?.settings?.voiceVolume ?? 1,
    });
    this.captions.preload(this._voiceUrls());
    this.objectives = new ObjectiveList({ title: definition.title });

    this._index = -1;
    this._objective = null;
    this._startDelay = TRAINING.startDelay;
    this._advanceTimer = -1;   // ≥ 0 while a cleared objective is easing out
    this._objTime = 0;
    this._holdTimer = 0;
    this._hintState = new Map();
    this._waypoint = null;     // resolved active waypoint, or null
    this._hudWaypoints = [];   // what GameHUD draws (0 or 1 entries)
    this._lastClearedOwner = null; // caption owner whose credit line survives the next cull
    // Every waypoint in the script, for TRAINING.debugWaypoints (see getHudWaypoints).
    this._allWaypoints = definition.objectives.map((o) => o.waypoint).filter(Boolean);
    this._finished = false;
    this._halted = false;

    // Reused per-frame snapshot handed to every predicate, so evaluating a
    // mission costs no allocations.
    this._ctx = {
      app, aircraft, controller, terrainCollider, enemyManager,
      mission: this,
      time: 0, elapsed: 0,
      groundSpeed: 0, airspeed: 0, airspeedKts: 0,
      altitude: 0, altitudeAGL: 0, verticalSpeed: 0,
      bank: 0, grounded: true, airborne: false,
      along: 0, lateral: 0,
      waypointRange: Infinity, waypointHeadingError: 0,
    };
    this._elapsed = 0;
  }

  /**
   * Waypoint markers for the spatial HUD — the active objective's, or the whole
   * pattern at once while TRAINING.debugWaypoints is on (a tuning aid: nothing
   * about how the mission advances changes, only what's drawn).
   */
  getHudWaypoints() {
    return TRAINING.debugWaypoints ? this._allWaypoints : this._hudWaypoints;
  }

  /** The active objective's definition, or null before the first / after the last. */
  get activeObjective() { return this._objective; }

  update(dt) {
    if (this._halted) return;

    // A wrecked airframe ends the lesson — the destroyed overlay owns the screen
    // from here, so pull the board and shut the instructor up.
    if (this.aircraft.destroyed) {
      this._halted = true;
      this.captions.clear();
      this.objectives.setVisible(false);
      return;
    }

    // Kept ticking past the last objective so any tail of the debrief still
    // plays out (and clears itself) after the mission has been signed off.
    this.captions.update(dt);
    if (this._finished) return;
    this._elapsed += dt;

    if (this._startDelay > 0) {
      this._startDelay -= dt;
      if (this._startDelay > 0) return;
      this._enter(0);
      return;
    }

    // Cleared objective easing off the board before the next one arrives.
    if (this._advanceTimer >= 0) {
      this._advanceTimer -= dt;
      if (this._advanceTimer <= 0) this._enter(this._index + 1);
      return;
    }

    const obj = this._objective;
    if (!obj) return;

    this._objTime += dt;
    this._refreshContext();
    this._updateWaypointReadout();
    this._updateHints(obj, dt);

    if (this._satisfied(obj)) {
      this._holdTimer += dt;
      if (this._holdTimer >= (obj.hold || 0)) this._clear(obj);
    } else {
      this._holdTimer = 0;
    }
  }

  // ── Objective lifecycle ────────────────────────────────────────────────────

  _enter(index) {
    const list = this.definition.objectives;
    this._objTime = 0;

    // Skip anything the player has already taken care of on their own — gear
    // already up, already parked on the centreline, already inside the next
    // waypoint. Handing out an instruction that's been carried out is worse
    // than useless: every one of these is a toggle, so a player dutifully
    // following the row would undo the thing the objective is waiting for.
    // An `info` beat has nothing to pre-empt, so it always plays.
    while (index < list.length && list[index].type !== 'info' && this._alreadyDone(list[index])) {
      index++;
    }
    if (index >= list.length) { this._finish(); return; }

    const obj = list[index];
    this._index = index;
    this._objective = obj;
    this._holdTimer = 0;
    this._advanceTimer = -1;
    this._hintState.clear();
    this._waypoint = obj.waypoint || null;
    this._hudWaypoints = this._waypoint ? [this._waypoint] : [];

    // Targets this step brings onto the field, in the scenario `spawns` shape.
    // Revealing them per-objective rather than all at launch keeps a lesson's
    // later range out of the HUD until the script gets to it — and note it
    // always hangs off the step *before* the one that shoots them, because
    // _targetsDown reads an unspawned id as already dead, which would skip the
    // `destroy` objective the instant it came up.
    if (obj.spawn) for (const entry of obj.spawn) this.enemyManager?.spawnEntry(entry);
    // After the spawn, so a marker tracking one of those targets is on it from
    // the first frame rather than wherever the previous run left the literal.
    if (this._waypoint) this._syncTrackedWaypoint(this._waypoint);

    if (obj.objective) this.objectives.add(obj.id, obj.objective);
    // This objective's briefing outranks anything still waiting from earlier
    // steps, so the backlog goes before it queues — otherwise a player working
    // quickly would hear each step's instructions a step or two too late. The
    // credit for the step just cleared is the one thing allowed to survive.
    this.captions.dropStale(this._lastClearedOwner);
    this.captions.enqueue(obj.audioCaptions, obj.id, { audio: this._voice(obj, 'captions') });
    obj.onEnter?.(this._refreshContext());
  }

  _clear(obj) {
    // Sticky, so the credit still lands even though the next objective is about
    // to clear the queue behind it (see the dropStale call in _enter).
    this._lastClearedOwner = `${obj.id}:done`;
    this.captions.enqueue(obj.onCompleteCaptions, this._lastClearedOwner, {
      sticky: true,
      audio: this._voice(obj, 'complete'),
    });
    this.objectives.complete(obj.id);
    obj.onComplete?.(this._ctx);

    this._objective = null;
    this._waypoint = null;
    this._hudWaypoints = [];
    this._advanceTimer = TRAINING.advanceDelay;
  }

  _finish() {
    this._finished = true;
    this._objective = null;
    this._hudWaypoints = [];
    this.onComplete?.();
  }

  // ── Completion ─────────────────────────────────────────────────────────────

  /**
   * Would `obj` be satisfied the instant it were handed out? Evaluated against
   * the objective's own waypoint (temporarily swapped in, since that's what the
   * context snapshot measures range from) and restored afterwards, so asking the
   * question leaves no trace on the active objective.
   */
  _alreadyDone(obj) {
    const previous = this._waypoint;
    this._waypoint = obj.waypoint || null;
    this._refreshContext();
    const done = this._satisfied(obj);
    this._waypoint = previous;
    return done;
  }

  _satisfied(obj) {
    // An info objective is "done talking" — everything else may still gate it.
    if (obj.type === 'info' && this.captions.isPending(obj.id)) return false;
    if (obj.type === 'destroy' && !this._targetsDown(obj)) return false;
    if (this._waypoint && !this._waypoint.guide && !this._inWaypoint()) return false;
    if (obj.complete && !obj.complete(this._ctx)) return false;
    return true;
  }

  _inWaypoint() {
    const wp = this._waypoint;
    const p = this.aircraft.position;
    const dx = p.x - wp.x;
    const dz = p.z - wp.z;
    const radius = wp.radius ?? TRAINING.waypoint.radius;
    if (dx * dx + dz * dz > radius * radius) return false;
    const vertical = wp.vertical ?? TRAINING.waypoint.vertical;
    return Math.abs(p.y - wp.y) <= vertical;
  }

  // Every named training target destroyed (or gone from the roster entirely).
  // The three flags are the three shapes a kill takes: ground vehicles latch
  // `destroyed` and leave a wreck in the roster, aircraft latch
  // `flight.structuralFailure` at the kill and only set `dead` once the wreck
  // has sunk below the terrain, and anything already pruned is simply absent.
  // Counting structural failure is what stops an air kill sitting on the board
  // for the ten seconds the wreck spends falling — and it matches how
  // EnemyManager scores the same event.
  _targetsDown(obj) {
    const names = obj.targets;
    const entities = this.enemyManager?.entities;
    if (!names || !entities) return true;
    for (const name of names) {
      const e = entities.find((x) => x.trainingId === name);
      if (e && !e.destroyed && !e.dead && !e.flight?.structuralFailure) return false;
    }
    return true;
  }

  // ── Hints ──────────────────────────────────────────────────────────────────

  _updateHints(obj, dt) {
    const hints = obj.hints;
    if (!hints) return;
    const cfg = TRAINING.hint;

    for (const hint of hints) {
      let st = this._hintState.get(hint.id);
      if (!st) { st = { armed: 0, cooldown: 0, count: 0 }; this._hintState.set(hint.id, st); }

      if (st.count >= (hint.max ?? cfg.max)) continue;
      if (st.cooldown > 0) { st.cooldown -= dt; continue; }

      // No `when` means a pure time-based nudge ("still waiting on you").
      if (hint.when && !hint.when(this._ctx)) { st.armed = 0; continue; }

      st.armed += dt;
      if (st.armed < (hint.after ?? cfg.after)) continue;
      // Wait for a gap in the briefing rather than queueing behind it — a nudge
      // that lands thirty seconds late is worse than one that never fires.
      if (this.captions.isSpeaking) continue;

      this.captions.enqueue(
        [{ caption: hint.caption, duration: hint.duration ?? cfg.duration }],
        `${obj.id}:${hint.id}`,
        { audio: this._voice(obj, `hint_${hint.id}`, hint.audio) },
      );
      st.armed = 0;
      st.cooldown = hint.cooldown ?? cfg.cooldown;
      st.count++;
    }
  }

  // ── Instructor voice ───────────────────────────────────────────────────────

  /**
   * URL of the recording for one group of caption lines, or null if the
   * objective isn't voiced (its captions then just play silently on their
   * authored durations). `own` is an explicit key that overrides the
   * objective-derived one — how a hint shared by several objectives points at
   * the single file recorded for it.
   */
  _voice(obj, suffix, own = null) {
    const key = own || (obj.audio ? `${obj.audio}_${suffix}` : null);
    return trainingVoiceUrl(this.definition.voice || this.definition.id, key);
  }

  /** Every file the script can speak, so they're all warm before the first cue. */
  _voiceUrls() {
    const urls = [];
    for (const obj of this.definition.objectives) {
      if (obj.audioCaptions?.length) urls.push(this._voice(obj, 'captions'));
      if (obj.onCompleteCaptions?.length) urls.push(this._voice(obj, 'complete'));
      for (const hint of obj.hints || []) {
        urls.push(this._voice(obj, `hint_${hint.id}`, hint.audio));
      }
    }
    return urls.filter(Boolean);
  }

  /** Freeze the instructor with the sim (the captions already ride sim time). */
  setPaused(paused) { this.captions.setPaused(paused); }

  /** User master-volume change from the settings overlay. */
  setMasterVolume(v) { this.captions.setMasterVolume(v); }

  /** User voice-volume change from the settings overlay. */
  setVoiceVolume(v) { this.captions.setVoiceVolume(v); }

  // ── Per-frame state ────────────────────────────────────────────────────────

  _refreshContext() {
    const a = this.aircraft;
    const c = this._ctx;
    const p = a.position;
    const v = a.velocity;

    c.time = this._objTime;
    c.elapsed = this._elapsed;
    c.groundSpeed = Math.hypot(v.x, v.z);
    c.airspeed = a.airspeed;
    c.airspeedKts = a.airspeed * 1.94384;
    c.altitude = p.y;
    c.altitudeAGL = a.altitudeAGL;
    c.verticalSpeed = v.y;
    c.grounded = a.isGrounded;
    c.airborne = !a.isGrounded;
    // Signed bank, matching the Controller's convention (positive = left wing up).
    c.bank = Math.atan2(a.rightVector.y, a.upVector.y);

    const rw = toRunway(p.x, p.z);
    c.along = rw.along;
    c.lateral = rw.lateral;

    if (this._waypoint) {
      this._syncTrackedWaypoint(this._waypoint);
      const dx = this._waypoint.x - p.x;
      const dz = this._waypoint.z - p.z;
      c.waypointRange = Math.hypot(dx, dz);
      // How far off the nose the marker sits, same cross/dot form and sign
      // convention as runwayHeadingError. Range on its own can't tell "lost,
      // pointing the wrong way" from "flying a long leg correctly", so hints
      // that nag about navigation key off this instead.
      const f = a.forwardVector;
      c.waypointHeadingError = Math.atan2(dx * f.z - dz * f.x, dx * f.x + dz * f.z);
    } else {
      c.waypointRange = Infinity;
      c.waypointHeadingError = 0;
    }
    return c;
  }

  /**
   * A waypoint carrying `track` follows a live enemy instead of sitting on a
   * fixed coordinate: its x/y/z are rewritten in place from the entity tagged
   * with that `trainingId`. Air targets move — a Hip in `explore` picks
   * waypoints anywhere in the world — so a static marker would send the player
   * at empty sky. Rewriting the waypoint itself rather than special-casing the
   * readers means everything downstream follows for free: the HUD marker, the
   * objective row's range readout, and the `waypointRange` /
   * `waypointHeadingError` the hints key off all read this same object.
   *
   * A target that is gone or dead leaves the marker at its last position,
   * which is where the wreck is going anyway.
   */
  _syncTrackedWaypoint(wp) {
    if (!wp.track) return;
    const e = this.enemyManager?.entities.find((x) => x.trainingId === wp.track);
    if (!e || e.dead || e.destroyed) return;
    const pos = e.flight?.position ?? e.hitVolume?.center;
    if (!pos) return;
    wp.x = pos.x;
    wp.y = pos.y;
    wp.z = pos.z;
  }

  // Live range on the objective row. This is the only navigation cue left when
  // the player has flown with the HUD switched off, so it's never suppressed.
  _updateWaypointReadout() {
    const obj = this._objective;
    if (!obj?.objective || !this._waypoint) return;
    const d = this._ctx.waypointRange;
    const text = d < 1000
      ? `${Math.round(d / 10) * 10} M`
      : `${(d * NM_PER_METER).toFixed(1)} NM`;
    this.objectives.setDetail(obj.id, text);
  }

  dispose() {
    this.captions.dispose();
    this.objectives.dispose();
    this._hudWaypoints = [];
  }
}
